import { arrToObject, updateArrayItem } from './array';
import { gather } from './function';
import { isEmpty } from './poly';
import { isDefined, isObject } from './type';

// Used because Object.values causes flow incompatibilities
// https://github.com/facebook/flow/issues/2221#issuecomment-372112477
export const values = <T>(obj: Readonly<Record<string, T>>): Array<T> =>
  Object.keys(obj).map((k) => obj[k]);

// Consistent with `values` above
// https://github.com/facebook/flow/issues/2221#issuecomment-372112477
// @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
export const entries = <S, T>(obj: Record<S, T>): Array<[S, T]> =>
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
  Object.keys(obj).map((k) => [k, obj[k]]);

/*
  Filter object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value). E.g.,
  filterObject({1: 2, 3: 4, 4: 10}, (k, v) => isOdd(k)) -> {1: 2, 3: 4}
*/
export const filterObject = <S, T>(
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
  obj: Readonly<Record<S, T>>,
  fn: (k: S, v: T) => boolean
) => arrToObject(Object.entries(obj).filter(gather(fn)));

/*
  Removes keys from `obj` whose values are null or undefined
*/
export const filterNulls = <S, T>(
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
  obj: Readonly<Record<S, T | null | undefined>>
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
): Record<S, T> => filterObject(obj, (_, v) => isDefined(v));

/*
  Map object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value), returning an array of length 2.
  mapObject({1: 2, 3: 4, 4: 10}, (k, v) => [k + 1, v + 1]) ->
  {2: 3, 4: 5, 5: 11}
*/
export const mapObject = <S, T, U>(
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
  obj: Readonly<Record<S, T>>,
  fn: (k: S, v: T) => [S, U | null | undefined]
) => arrToObject(Object.entries(obj).map(gather(fn)));

/*
  Same as mapObject, but the function operates on just the values.
  Passed function can take the key as the second argument if needed.
  mapValues({1: 2, 3: 4, 4: 10}, v => v + 1) -> {1: 3, 3: 5, 4: 11}
*/
export const mapValues = <S, T, U>(
  // @ts-expect-error ts-migrate(2344) FIXME: Type 'S' does not satisfy the constraint 'string |... Remove this comment to see the full error message
  obj: Readonly<Record<S, T>>,
  fn: (v: T, k?: S) => U | null | undefined
) => mapObject(obj, (k, v) => [k, fn(v, k)]);
export const objectEmpty = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  o: Record<string, any> | null | undefined
): boolean => !isObject(o) || isEmpty(Object.keys(o));
export const updateObject = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  oldObject: Record<string, any>,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...updates: Array<Record<string, any>>
) => Object.assign({}, oldObject, ...updates);
export const removeKeys = (
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'unknown' is not assignable to type 'string |... Remove this comment to see the full error message
  obj: Readonly<{ [key in unknown]?: unknown }>,
  keys: Array<unknown>
) => filterObject(obj, (k) => !keys.includes(k));
// Get an item in a nested object. E.g., getIn({a: {b: 2}}, ['a', 'b']) => 2
export const getIn = <T>(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>,
  accessor: ReadonlyArray<number | string> | string,
  defaultValue: T | null | undefined = null
): T | null | undefined => {
  const acc = accessor instanceof Array ? accessor : accessor.split('.');
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Record<string, any>' is not assignable to ty... Remove this comment to see the full error message
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  return isEmpty(acc)
    ? defaultValue
      ? object || defaultValue
      : object
    : object
    ? getIn(object[acc[0]], acc.slice(1), defaultValue)
    : defaultValue;
};
// Update an item in a nested object. E.g., updateIn({a: {b: 2}}, ['a', 'b'], x => x + 1) => {a: {b: 3}}
// If any levels do not exist, objects will be created
// Does not modify the original object
export const updateIn = <T>(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>,
  accessor: ReadonlyArray<string>,
  operation: (arg0: T) => T
) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  isEmpty(accessor) // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Record<string, any>' is not assi... Remove this comment to see the full error message
    ? operation(object) // TODO: Fix this the next time the file is edited.
    : // eslint-disable-next-line no-nested-ternary
    !object
    ? updateIn({}, accessor, operation)
    : Array.isArray(object) // handle 'objects' and 'arrays' with separate update operations
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateArrayItem<Record<string, any>>(
        object,
        Number(accessor[0]),
        updateIn(object[accessor[0]], accessor.slice(1), operation)
      )
    : updateObject(object, {
        [accessor[0]]: updateIn(
          object[accessor[0]],
          accessor.slice(1),
          operation
        ),
      });
// Invert an object
// Note that values of object must be hashable (e.g., not mutable)
// If keys in original map are not strings, they are cast to strings as a result of
// Object.keys / Object.entries
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const invert = (object: Record<string, any>) =>
  mapObject(object, (k, v) => [v, k]);
export const getter =
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, @typescript-eslint/no-explicit-any
  (attr: string) => (x: Record<string, any> | null | undefined) =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/destructuring-assignment
    x ? x[attr] : null;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectKeys = (object: Record<string, any>, keys: Array<string>) =>
  filterObject(object, (k) => keys.includes(k));
